import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { repeat } from '@nullvoxpopuli/ember-composable-helpers';
import type { Task } from 'ember-concurrency';
import { eq, notEq } from 'ember-truth-helpers';
import preventDefault from 'garaje/helpers/prevent-default';
import focusOnInsert from 'garaje/modifiers/focus-on-insert';
import onBackspace from 'garaje/modifiers/on-backspace';
import { TrackedArray } from 'tracked-built-ins';

interface MFAChallengeComponentSignature {
  Args: {
    changeEmail: (event: Event) => void;
    sendMfaCode: Task<void, []>;
    verifyMfaCode: Task<void, [string, boolean, boolean?]>;
  };
}

const CODE_LENGTH = 6;

export default class MFAChallengeComponent extends Component<MFAChallengeComponentSignature> {
  formId = `${guidFor(this)}-form`;

  @tracked code = new TrackedArray<string>(new Array(CODE_LENGTH).fill(''));

  handleInput = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const value = input?.value;
    const index = parseInt(input.dataset['index'] as string);

    // only 1 digitnumbers, ${CODE_LENGTH} digit numbers or blank (backspace) values are allowed
    const validValueRegex = new RegExp(`^$|^\\d$|^\\d{${CODE_LENGTH}}$`);
    if (!validValueRegex.test(value)) {
      input.value = this.code[index] ?? ''; // maintain value on invalid inout
      input.select();
      return;
    }

    if (value.length === 0) {
      // backspace input
      this.code[index] = value;
    } else if (value.length === 1) {
      // single digit input
      this.code[index] = value;

      // focus on the next input if not at the end or deleting this input's value
      const nextInput = input.nextElementSibling as HTMLInputElement | null;
      if (nextInput && value !== '') nextInput.focus();
    } else if (index === 0) {
      // copy pasting a multi-digit code in the first input
      Array.from(value).forEach((digit, idx) => {
        const input = document.querySelector(`#${this.formId} input[data-index="${idx}"]`) as HTMLInputElement;
        if (input) input.value = digit;
        this.code[idx] = digit;
      });

      // focus the last code input
      const lastInput = document.querySelector(`#${this.formId} input[data-index="${CODE_LENGTH - 1}"]`);
      if (lastInput) (lastInput as HTMLInputElement).focus();
    } else {
      // noop, pasting a multi digit code into an input other than the first
      input.value = this.code[index] ?? '';
      input.select();
    }

    // if it is a full code try verifying
    if (this.mfaCode.length === CODE_LENGTH) {
      this.verifyMfaCode(true);
    }
  };

  handleBackspace = (event: KeyboardEvent): void => {
    const input = event.target as HTMLInputElement;
    const prevInput = input.previousElementSibling as HTMLInputElement | null;

    // focus the previous input if current input is empty
    if (input.value === '') prevInput?.focus();
  };

  selectInputContent = (event: FocusEvent): void => {
    const input = event.target as HTMLInputElement;

    requestAnimationFrame(() => {
      input.select();
    });
  };

  verifyMfaCode = (quiet: boolean): void => {
    const rememberDeviceElement = <HTMLInputElement>(
      document.querySelector(`#${this.formId} input[name="remember-device"]`)
    );
    const rememberDevice = rememberDeviceElement?.checked ?? false;
    void this.args.verifyMfaCode.perform(this.mfaCode, rememberDevice, quiet);
  };

  get mfaCode(): string {
    return this.code.join('');
  }

  <template>
    <form
      class="w-full md:w-[420px]"
      id={{this.formId}}
      {{on "submit" (preventDefault (fn this.verifyMfaCode false))}}
      data-test-mfa-challenge-form
    >
      <h3 class="text-center mb-4">Multifactor Authentication Required</h3>
      <div>
        <p class="text-center">We've emailed you a one-time code. Please enter the code below to finish logging in.</p>
        <p class="text-center">
          <strong>Important:</strong>
          Do not share this code with anyone.<br />Envoy employees will never ask you for this code.
        </p>
        <div class="w-full md:w-80 p-0 m-auto">
          <div
            class="!mt-4 !mb-4 grid grid-cols-6 gap-x-2 md:gap-x-4 content-center"
            id="mfa-code-input"
            data-test-mfa-code-input
          >
            {{#each (repeat CODE_LENGTH) as |_ index|}}
              <input
                class="text-center !text-2xl !font-bold !mb-2 selection:bg-transparent"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete={{if (eq index 0) "one-time-code"}}
                data-index={{index}}
                {{on "input" this.handleInput}}
                {{onBackspace this.handleBackspace}}
                {{on "focus" this.selectInputContent}}
                {{(if (eq index 0) (modifier focusOnInsert))}}
              />
            {{/each}}
          </div>
          <div class="mt-1 w-full flex justify-center space-x-1">
            <input class="w-4 h-4" id="remember-device" type="checkbox" name="remember-device" checked />
            <label for="remember-device">
              Remember me for 30 days
              <br />
              <span class="text-grey">not recommended on shared or public devices</span>
            </label>
          </div>
        </div>
        <button
          type="submit"
          class="btn btn--medium btn--brand w-full mt-4"
          data-canary="submit-mfa-button"
          data-test-submit-mfa-button
          disabled={{notEq this.code.length CODE_LENGTH}}
        >
          <span class="lock"></span>
          Verify
        </button>
      </div>
    </form>

    <div class="mt-4 text-center">
      <button
        data-test-send-mfa-code
        type="button"
        class="btn btn--link !text-xs !text-carbon-50"
        {{on "click" @sendMfaCode.perform}}
      >
        Resend Code
      </button>
      <button
        data-test-change-email
        type="button"
        class="btn btn--link !text-xs !text-carbon-50 ml-4"
        {{on "click" @changeEmail}}
      >
        Change email
      </button>
    </div>
  </template>
}
